import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckBox,
  controlToastState,
  Modal,
  TextInput,
} from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import {
  getDocumentNameForStatusAndDocType,
  getDocumentTitleByDocType,
} from "../../../constants/statusToDocMap/statusToDocMap";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { DocumentsStoreedInLocalStorage } from "../../../data-model/docsStorageInLocalStorage";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { ModalId } from "../../../enums/modalId";
import { useFileToBase64 } from "../../../hooks/useFileToBase64";
import { useMergePdfs } from "../../../hooks/useMergePdfs";
import {
  resetFileUploadState,
  uploadFileToFirebaseStorage,
} from "../../../redux/slice/fileUploadSlice";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { AddMore } from "../../../svg/add-more";
import { DocUploadIcon } from "../../../svg/doc-upload-icon";
import { getEventNameByDocTypeWithFallback } from "../../../utils/documentTypeToEventName";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { updateJcodesWithCorrectDocRef } from "../../../utils/updateJcodesWithCorrectDocRef";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import NewFileUploaderCard from "../../newFileUploader/newFileUploader";

interface NarLetterModalProps {
  onClose: () => void;
  title?: string;
  showDragDropUpload?: boolean;
  authStatus?: string;
  shouldLogEvent?: boolean;
}

const NarLetterModal = (props: NarLetterModalProps) => {
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [selectedMedicines, setSelectedMedicines] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [docMetaData, setDocMetaData] = useState<
    DocumentsStoreedInLocalStorage[]
  >([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [documentTitle, setDocumentTitle] = useState<string>("");
  const { progress, downloadURL } = useSelector(
    (state: RootState) => state.fileUpload,
  );
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { metaData } = useSelector((state: RootState) => state.modal);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const orgID = getOrgIdForFetchExternalWorklist();
  const dispatch = useDispatch<AppDispatch>();
  const uuid = useMemo(() => uuidv4(), []);
  const fileUploadPath = useMemo(() => {
    return `medical_pa_order/${singleMedicalPaOrder?.id}/j_code_docs/${uuid}.pdf`;
  }, [singleMedicalPaOrder?.id, uuid]);

  // Use hooks for file conversion and merging
  const { base64Urls, convertFilesToBase64, clearBase64Urls } = useFileToBase64(
    undefined,
    false,
  );
  const { mergedFile, mergePdfsFromBase64, isMerging } = useMergePdfs();

  const isEventLogged = useRef(false);
  const logEventTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMergingRef = useRef(false);

  const debouncedLogEvent = useCallback((eventData: any) => {
    if (logEventTimeoutRef.current) {
      clearTimeout(logEventTimeoutRef.current);
    }
    logEventTimeoutRef.current = setTimeout(() => {
      apiWorkerManager.enqueueAndProcess(eventData);
      logEventTimeoutRef.current = null;
    }, 850);
  }, []);

  useEffect(() => {
    const allDocsGroupedByType = getItemFromLocalStorage(
      `${LocalStorageKeys.JCode_Docs}_${singleMedicalPaOrder?.id}`,
    );
    const requiredMedicineNames =
      allDocsGroupedByType?.[metaData?.docType ?? ""];
    if (requiredMedicineNames && requiredMedicineNames.length === 1) {
      setSelectedMedicines([requiredMedicineNames[0]?.jcodeIdentifier ?? ""]);
    }
    setDocMetaData(requiredMedicineNames ?? []);
  }, [metaData?.authStatus]);

  useEffect(() => {
    if (props?.shouldLogEvent && !isEventLogged.current) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name: EventsName.MODAL_SHOWN,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          doc_type: metaData?.docType ?? "",
          payer_name: payerName,
        },
      });
      isEventLogged.current = true;
    }
  }, [props?.shouldLogEvent]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
    const duplicateFiles = newFiles.filter((newFile) =>
      uploadedFiles.some((existingFile) => existingFile.name === newFile.name),
    );

    if (duplicateFiles.length > 0) {
      controlToastState("duplicate-file-upload");
      return;
    }

    const uniqueFiles = newFiles.filter(
      (newFile) =>
        !uploadedFiles.some(
          (existingFile) => existingFile.name === newFile.name,
        ),
    );

    setFileNames((prev) => [...prev, ...uniqueFiles.map((file) => file.name)]);
    setUploadedFiles((prev) => [...prev, ...uniqueFiles]);
  };

  const handleMedicineSelection = (
    medicineName: string,
    isChecked: boolean,
  ) => {
    setSelectedMedicines((prev) => {
      const newMedicines = [...prev];
      if (isChecked) {
        newMedicines.push(medicineName);
      } else {
        newMedicines.splice(newMedicines.indexOf(medicineName), 1);
      }

      return newMedicines;
    });
  };

  const logEventForCheckBoxClick = (
    medicineName: string,
    isChecked: boolean,
  ) => {
    if (props?.shouldLogEvent && isEventLogged.current) {
      const getNameOfMedicinesFromOptions = docMetaData.find(
        (doc) => doc.jcodeIdentifier === medicineName,
      )?.jcodeDescription;

      props?.shouldLogEvent &&
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: orgID ?? "",
          event_name:
            EventsName.CHECK_BOX_CLICKED +
            " (" +
            getNameOfMedicinesFromOptions +
            " - " +
            isChecked +
            ")",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
            doc_type: metaData?.docType ?? "",
            payer_name: payerName,
          },
        });
    }
  };

  const handleOnSave = async () => {
    if (uploadedFiles.length > 0) {
      getDocumentNameForStatusAndDocType(
        metaData?.authStatus ?? "",
        metaData?.docType ?? "",
      );
      setIsUploading(true);
      try {
        await convertFilesToBase64(uploadedFiles);
      } catch (error) {
        controlToastState("error-nar-letter-upload");
        setIsUploading(false);
        isMergingRef.current = false;
        return;
      }
    } else {
      dispatch(closeModal());
      props.onClose();
    }
  };

  const handleFileDelete = (index: number) => {
    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });

    setFileNames((prevFileNames) => {
      const newFileNames = [...prevFileNames];
      newFileNames.splice(index, 1);
      return newFileNames;
    });

    // Reset the file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Effect to merge PDFs after base64 conversion
  useEffect(() => {
    const mergePdfs = async () => {
      if (
        base64Urls.length > 0 &&
        isUploading &&
        !mergedFile &&
        !isMergingRef.current
      ) {
        isMergingRef.current = true;
        const docType = getDocumentNameForStatusAndDocType(
          metaData?.authStatus ?? "",
          metaData?.docType ?? "",
        );
        const fileName = `${docType}.pdf`;

        try {
          await mergePdfsFromBase64(base64Urls, fileName);
          // Clear base64 URLs from memory after successful merge to free up memory
          clearBase64Urls();
        } catch (error) {
          controlToastState("error-nar-letter-upload");
          setIsUploading(false);
          isMergingRef.current = false;
          // Clear base64 URLs even on error to free up memory
          clearBase64Urls();
        }
      }
    };

    mergePdfs();

    // Cleanup function to reset the ref if component unmounts or dependencies change
    return () => {
      // Only reset if we haven't successfully merged yet
      if (!mergedFile && isMergingRef.current) {
        isMergingRef.current = false;
      }
    };
  }, [
    base64Urls,
    isUploading,
    mergedFile,
    metaData?.authStatus,
    metaData?.docType,
    mergePdfsFromBase64,
    clearBase64Urls,
  ]);

  // Effect to upload merged PDF to Firebase
  useEffect(() => {
    if (mergedFile && isUploading) {
      dispatch(uploadFileToFirebaseStorage(mergedFile, fileUploadPath));
    }
  }, [mergedFile, isUploading, dispatch, fileUploadPath]);

  useEffect(() => {
    if (downloadURL && isUploading) {
      updateJCodesWithCorrectDocData();
    }
  }, [downloadURL, isUploading]);

  const updateJCodesWithCorrectDocData = async () => {
    const updatedJCodes = updateJcodesWithCorrectDocRef(
      singleMedicalPaOrder?.auth_on_file?.auth_entries ?? [],
      selectedMedicines,
      metaData?.docType ?? "",
      fileUploadPath,
      documentTitle,
      user?.email ?? "",
    );
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.medicalPaOrders(),
        singleMedicalPaOrder?.id ?? "",
        {
          "auth_on_file.auth_entries": updatedJCodes,
        },
      );
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name: getEventNameByDocTypeWithFallback(metaData?.docType ?? ""),
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          doc_type: metaData?.docType ?? "",
          doc_title: documentTitle,
          doc_path: fileUploadPath,
          payer_name: payerName,
        },
      });
      setIsUploading(false);
      isMergingRef.current = false;
      controlToastState("nar-letter-uploaded");
      dispatch(resetFileUploadState());
      dispatch(closeModal());
    } catch (error) {
      console.error(error);
      setIsUploading(false);
      isMergingRef.current = false;
    }
  };

  return (
    <Modal
      dialogId={ModalId.NAR_LETTER_MODAL}
      title={getDocumentTitleByDocType(metaData?.docType ?? "")}
      onClose={() => {
        dispatch(resetFileUploadState());
        dispatch(closeModal());
        props.onClose();
      }}
      onCancel={() => {
        dispatch(resetFileUploadState());
        dispatch(closeModal());
        props.onClose();
      }}
      onSave={handleOnSave}
      saveButtonText="Save"
      cancelText="Cancel"
      disableSave={
        isUploading ||
        isMerging ||
        !documentTitle.trim() ||
        selectedMedicines.length === 0 ||
        uploadedFiles.length === 0
      }
    >
      <div className="flex flex-col gap-5 p-1">
        <TextInput
          id="document-title-input"
          label="Document Title (Enter as per OncoEMR)"
          onChange={(data) => {
            setDocumentTitle(data.value);
            debouncedLogEvent({
              patient_id: singleMedicalPaOrder?.patient_id ?? "",
              order_id: singleMedicalPaOrder?.order_id ?? "",
              org_id: orgID ?? "",
              event_name: EventsName.DOCUMENT_TITLE_UPDATED,
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
                doc_type: metaData?.docType ?? "",
                doc_title: data.value,
                payer_name: payerName,
              },
            });
          }}
          defaultValue={documentTitle}
        />
        <div className="text-tiny">
          Select Drugs for which this document is valid
          <div className="mt-2 flex flex-col gap-2">
            {docMetaData?.map((doc) => (
              <CheckBox
                key={doc?.jcodeIdentifier ?? ""}
                label={doc?.jcodeDescription ?? ""}
                id={doc?.jcodeIdentifier ?? ""}
                onCheckBoxValueChange={(data) => {
                  handleMedicineSelection(
                    doc?.jcodeIdentifier ?? "",
                    data.value,
                  );
                  logEventForCheckBoxClick(
                    doc?.jcodeIdentifier ?? "",
                    data.value,
                  );
                }}
                defaultValue={
                  (doc?.isDocPresent || docMetaData.length === 1) ?? false
                }
                isDisabled={doc?.isDocPresent ?? false}
              />
            ))}
          </div>
        </div>
        <div>
          <div className="documents-modal--container flex flex-col gap-3">
            <div
              className="documents-modal--add-more mt-2 flex items-center gap-1 hover:cursor-pointer"
              onClick={() => {
                handleClick();
              }}
            >
              {props.showDragDropUpload ? (
                <div className="flex h-32 w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-primaryGray-15 p-2">
                  <DocUploadIcon />
                  <div className="main-text text-sm">
                    Drag and drop or{" "}
                    <span className="text-tertiaryBlue-4">click here</span> to
                    upload
                  </div>
                </div>
              ) : (
                <div className="flex flex-row items-center gap-1">
                  <AddMore />

                  <div className="documents-modal--add-more--text text-h12 font-semibold text-tertiaryBlue-4">
                    Add More
                  </div>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                multiple
                className="hidden"
                onChange={handleFileChange}
                accept=".pdf"
                onClick={() => {
                  props?.shouldLogEvent &&
                    apiWorkerManager.enqueueAndProcess({
                      patient_id: singleMedicalPaOrder?.patient_id ?? "",
                      order_id: singleMedicalPaOrder?.order_id ?? "",
                      org_id: orgID ?? "",
                      event_name: EventsName.UPLOAD_DOCUMENT,
                      user_id: user?.id ?? "",
                      user_email: user?.email ?? "",
                      additional_data: {
                        screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
                        doc_type: metaData?.docType ?? "",
                        payer_name: payerName,
                      },
                    });
                }}
              />
            </div>
            {fileNames.map((docName, index) => (
              <NewFileUploaderCard
                key={`${docName}-${index}`}
                fileName={docName}
                progress={progress}
                isUploading={isUploading}
                handleDelete={() => {
                  handleFileDelete(index);
                  props?.shouldLogEvent &&
                    apiWorkerManager.enqueueAndProcess({
                      patient_id: singleMedicalPaOrder?.patient_id ?? "",
                      order_id: singleMedicalPaOrder?.order_id ?? "",
                      org_id: orgID ?? "",
                      event_name: EventsName.DELETE_DOCUMENT_CLICKED,
                      user_id: user?.id ?? "",
                      user_email: user?.email ?? "",
                      additional_data: {
                        screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
                        doc_type: metaData?.docType ?? "",
                        payer_name: payerName,
                      },
                    });
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NarLetterModal;
